import { getMomentTz, TimeService, HelmetDexteel } from "@dexteel/mesf-core";
import { closeWorkOrder } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import type { ModalWorkOrder } from "@/shared/work-orders-modals/models/ModalWorkOrder";
import { DateTimePicker } from "@mui/x-date-pickers";

import { ErrorModal, MESApiService, useToken } from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useWorkOrdersContext } from "../../../pages/work-orders/context/workOrdersContext";
import type { WorkOrder } from "../../../pages/work-orders/models/WorkOrder";
import { Controller, useForm } from "react-hook-form";
import { INITIAL_VALUES_MODAL } from "@/shared/work-orders-modals/models/ModalInitialValue";
import { CraftPicker } from "./picker/craftPicker";
import { useGetAssetPath } from "@/shared/hooks/useGetAssetFullPath";
import { TableWorkOrdesResoures } from "./work-order-resources/table-work-ordes-resoures";
import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	data: ModalWorkOrder | null;
	suffixTitle?: string;
};

export function CloseWorkOrderModalWithAttachments({
	show,
	onHide,
	data,
	suffixTitle,
}: Props) {
	const { getUserName } = useToken();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [linkAttachmentError, setLinkAttachmentError] = useState<string>("");
	const [closeSuccess, setCloseSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [updateWhenClose, setUpdateWhenClose] = useState(false);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [openWorkOrdersResources, setOpenWorkOrdersResources] = useState(false);
	const [assetChanged, setAssetChanged] = useState(false);

	const {
		state,
		state: {
			modalWorkOrderSelected: {
				Comments,
				TemplateTitle,
				WorkOrderPlannedHours,
				WorkOrderOpenedBy,
				WorkOrderName,
				Title,
				AssetName,
				DownHours,
				WorkOrderId,
				AssetId,
			},
		},
		actions: {
			setComments,
			setWorkOrderCloseTimestamp,
			setWorkOrderNumberOfWorkers,
			setWorkOrderConsumedHours,
			setTemplateTitle,
			setWorkOrderPlannedHours,
			setWorkOrderOpenedBy,
			setOpenTimestamp,
			setWorkOrderName,
			setTitle,
			setAssetName,
			setWorkOrderId,
			setDownHours,
			setAttachmentsLinked,
			setAssetId,
			setCraftId,
			setTypeCode,
		},
	} = useWorkOrderModalsContext();
	const [isLoadingAssetPath, setIsLoadingAssetPath] = useState<boolean>(true);
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");

	const { GetAssetFullPath } = useGetAssetPath({
		setIsLoadingAssetPath,
		setErrorAssetPath,
		setAssetName,
	});
	const {
		state: { selectedFilesToAdd },
		actions: { setSelectedFilesToAdd, setAttachmentsToAddToWorkOrder },
	} = useWorkOrdersContext();

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<ModalWorkOrder>({ defaultValues: INITIAL_VALUES_MODAL });

	const [userInput, setUserInput] = useState<string>("");
	const api = new MESApiService();

	//functions
	const appendComment = (newComment: string) => {
		const now = moment(new Date()).format("MM/DD/YYYY HH:mm");
		let newValue = "";

		if (Comments === "" || Comments === null || Comments === "null") {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				Comments + "\n" + getUserName() + " - " + now + " - " + newComment;
		}
		if (newComment != "") {
			setUserInput("");
			setComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setCloseSuccess(false);
	};

	const handleCancel = () => {
		onHide(false);
	};

	const handleWorkOrderResourcesDialog = () => {
		setOpenWorkOrdersResources(true);
	};

	const title = watch("Title");
	const changeTitle = () => {
		setTitle(title);
	};

	const closeTimestamp = watch("CloseTimestamp");
	const changeCloseTimestamp = () => {
		setWorkOrderCloseTimestamp(closeTimestamp);
	};

	const workOrderNumberOfWorkers = watch("WorkOrderNumberOfWorkers");
	const changeWorkOrderNumberOfWorkers = () => {
		setWorkOrderNumberOfWorkers(
			Number.parseInt(workOrderNumberOfWorkers as any),
		);
	};

	const consumedHours = watch("ConsumedHours");
	const changeConsumedHours = () => {
		setWorkOrderConsumedHours(consumedHours);
	};

	const onSubmit = async () => {
		setIsSubmitLoading(true);
		if (consumedHours) {
			const consumedHoursValue = (consumedHours as number)
				.toString()
				.replace(",", ".");
			setWorkOrderConsumedHours(Number.parseFloat(consumedHoursValue));
		}
		const res = await closeWorkOrder(state.modalWorkOrderSelected as WorkOrder);
		if (res.ok) {
			if (selectedFilesToAdd.length >= 1)
				await api
					.uploadFiles(selectedFilesToAdd, "Attachments")
					.then((res) => setAttachmentsToAddToWorkOrder(res.result));
			setIsSubmitLoading(false);
			reset();
			setCloseSuccess(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	const changeComments = (value: string) => {
		setComments(value);
	};

	const formatDescription = (description: string): string => {
		if (description?.includes("/") && description.split("/").length > 3) {
			const parts = description.split("/");
			let formattedDescription = "";

			for (let i = 0; i < parts.length; i++) {
				if (i < parts.length - 2) {
					formattedDescription += ".../";
				} else if (i === parts.length - 2) {
					if (parts[i].length > 6) {
						formattedDescription += parts[i].substring(0, 6) + ".../";
					} else {
						formattedDescription += parts[i] + "/";
					}
				} else {
					formattedDescription += parts[i];
					if (i < parts.length - 1) {
						formattedDescription += "/";
					}
				}
			}

			return formattedDescription;
		} else {
			return description;
		}
	};

	const description = useMemo(
		() => formatDescription(AssetName as string),
		[AssetName],
	);

	useEffect(() => {
		if (data != null && show) {
			setComments(data?.Comments);
			setTemplateTitle(data?.TemplateTitle);
			setWorkOrderPlannedHours(data?.WorkOrderPlannedHours);
			setWorkOrderNumberOfWorkers(data?.WorkOrderNumberOfWorkers);
			setWorkOrderOpenedBy(data?.WorkOrderOpenedBy);
			setOpenTimestamp(data?.OpenTimestamp);
			setWorkOrderName(data?.WorkOrderName);
			setTitle(data?.Title);
			GetAssetFullPath((data?.AssetId as number)?.toString());
			setAssetId(data?.AssetId);
			setWorkOrderId(data?.WorkOrderId);
			setDownHours(data?.DownHours);
			setWorkOrderConsumedHours(data?.ConsumedHours);
			setWorkOrderCloseTimestamp(data?.CloseTimestamp);
			setAttachmentsLinked(data?.AttachmentsLinked);
			setCraftId(data?.CraftId);
			setTypeCode(data?.TypeCode);
			setSelectedFilesToAdd([]);
			setValue("WorkOrderNumberOfWorkers", data?.WorkOrderNumberOfWorkers);
			setValue("ConsumedHours", data?.ConsumedHours);
			setValue("CloseTimestamp", moment().toDate());
			setValue("CraftId", data?.CraftId);
			setValue("Title", data?.Title);

			setUserInput("");
			setIsSubmitLoading(false);
		} else {
			if (updateWhenClose) {
				onHide(true);
			} else {
				onHide(false);
			}
			setUpdateWhenClose(false);
		}
	}, [show]);

	useEffect(() => {
		changeTitle();
	}, [title]);

	useEffect(() => {
		changeWorkOrderNumberOfWorkers();
	}, [workOrderNumberOfWorkers]);

	useEffect(() => {
		changeConsumedHours();
	}, [consumedHours]);

	useEffect(() => {
		changeCloseTimestamp();
	}, [closeTimestamp]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Close Work Order${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<DialogTitle
						sx={{
							padding: "15px 40px 10px 24px",
							marginRight: 2.5,
							width: "100%",
						}}
					>
						<Typography sx={{ fontSize: "1.7em", fontWeight: "bold" }}>
							CLOSE: {WorkOrderName}
						</Typography>
						{TemplateTitle && <Typography>{data?.TemplateTitle}</Typography>}
					</DialogTitle>
					<DialogContent
						dividers
						sx={{
							paddingTop: "0px",
						}}
					>
						<Grid2 container spacing={1.25}>
							<Grid2 sx={{ padding: 0, marginTop: 1.25 }} size={{ xs: 12 }}>
								<TextField
									label="Title"
									variant="outlined"
									disabled
									value={Title}
									fullWidth
									autoComplete="off"
								/>
							</Grid2>
							<Grid2
								container
								sx={{ margin: "-5px 0 0 0", padding: 0 }}
								spacing={2}
								size={{ xs: 12 }}
							>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 6, xs: 12 }}>
									<TextField
										label="Asset"
										variant="outlined"
										disabled
										value={description || ""}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 12 }}>
									<TextField
										label="Opened by"
										variant="outlined"
										disabled
										value={WorkOrderOpenedBy}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
							</Grid2>
							<Grid2
								container
								sx={{ margin: "-5px 0 0 0", padding: 0 }}
								spacing={2}
								size={{ xs: 12 }}
							>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 3, xs: 6 }}>
									<TextField
										label="Planned Hours:"
										variant="outlined"
										disabled
										value={WorkOrderPlannedHours}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 6 }}>
									<TextField
										label="DownHours"
										variant="outlined"
										disabled
										value={DownHours}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 6 }}>
									<Controller
										name="CraftId"
										control={control}
										render={({ field: { onChange, value } }) => (
											<CraftPicker
												value={value as number}
												onChange={onChange}
												disabled={true}
											/>
										)}
									/>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 3, xs: 6 }}>
									<TextField
										label="Opened"
										variant="outlined"
										disabled
										value={moment(data?.OpenTimestamp).format(
											"MM/DD/YYYY HH:mm A",
										)}
										fullWidth
										autoComplete="off"
									/>
								</Grid2>
							</Grid2>
							<Grid2
								container
								sx={{ margin: "-5px 0 0 0", padding: 0 }}
								spacing={2}
								size={{ xs: 12 }}
							>
								<Grid2 sx={{ paddingLeft: 0 }} size={{ md: 4, xs: 6 }}>
									<Grid2 size={{ xs: 12, md: 12 }}>
										<Controller
											name="CloseTimestamp"
											control={control}
											rules={{
												required: true,
												validate: (value) => {
													if (value === null || value === undefined) {
														return "Date is required";
													}
													const currentDate = new Date();
													return (
														value <= currentDate ||
														"Date cannot be in the future"
													);
												},
											}}
											render={({ field }) => (
												<DateTimePicker
													label="CloseTimestamp"
													timezone={TimeService.getInstance().getServerTimeZone()}
													format="MM/DD/YYYY HH:mm"
													timeSteps={{ minutes: 1 }}
													value={moment(field.value) as any}
													onChange={field.onChange}
													inputRef={field.ref}
													slotProps={{
														textField: {
															fullWidth: true,
															variant: "outlined",
															margin: "dense",
															error: !!errors.CloseTimestamp,
															sx: {
																"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
																	{
																		border: "2px solid #3F51B5",
																	},
															},
														},
													}}
												/>
											)}
										/>
										{errors.CloseTimestamp && (
											<Typography
												sx={{
													fontSize: 12,
													color: "#F44336",
													margin: 0,
												}}
											>
												{typeof errors.CloseTimestamp.message === "string"
													? errors.CloseTimestamp.message
													: "Close date cannot be in the future"}
											</Typography>
										)}
									</Grid2>
								</Grid2>
								<Grid2 size={{ md: 4, xs: 6 }}>
									<Controller
										name="ConsumedHours"
										control={control}
										rules={{
											pattern: {
												value: /^(?:0|[1-9]\d*)$/,
												message: "Enter a valid number",
											},
										}}
										render={({ field: { onChange, value } }) => (
											<TextField
												label="Consumed Hours"
												variant="outlined"
												error={!!errors.ConsumedHours}
												fullWidth
												autoComplete="off"
												value={value}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															border: "2px solid #3F51B5",
														},
												}}
											/>
										)}
									/>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 4, xs: 6 }}>
									<Controller
										name="WorkOrderNumberOfWorkers"
										control={control}
										rules={{
											pattern: {
												value: /^-?\d*(.\d+)?$/,
												message: "Enter a valid number",
											},
										}}
										render={({ field: { onChange, value } }) => (
											<TextField
												label="Workers"
												variant="outlined"
												error={!!errors.WorkOrderNumberOfWorkers}
												fullWidth
												autoComplete="off"
												value={value}
												onChange={(e) => onChange(e)}
												sx={{
													"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
														{
															border: "2px solid #3F51B5",
														},
												}}
											/>
										)}
									/>
								</Grid2>
							</Grid2>
						</Grid2>
						<Grid2 container size={{ xs: 12 }}>
							<Grid2
								sx={{
									marginBottom: 0.5,
									marginTop: 1.5,
									padding: 0,
								}}
								size={{ xs: 12 }}
							>
								<Card variant="outlined" sx={{ backgroundColor: "#E9F2F8" }}>
									<CardContent sx={{ padding: "12px !important" }}>
										<Typography
											sx={{
												fontSize: 15,
												fontWeight: "bold",
												color: "#0000008A",
											}}
										>
											Comments:
										</Typography>
										<SimpleTextAreaControl
											value={Comments || ""}
											onChange={changeComments}
											disabled
											showColor={false}
											styleText={{
												height: "70px",
												border: "none",
												backgroundColor: "#E9F2F8",
												fontSize: 16,
											}}
										/>
									</CardContent>
								</Card>
							</Grid2>
							<Grid2
								container
								spacing={2}
								alignItems="center"
								size={{ xs: 12 }}
								sx={{ margin: "0 -8px" }}
							>
								<Grid2 sx={{ padding: "0 8px" }} size={{ md: 9, xs: 12 }}>
									<TextField
										variant="outlined"
										multiline
										maxRows={3}
										value={userInput}
										onChange={(e) => setUserInput(e.target.value)}
										label="Comment"
										fullWidth
										onKeyDown={onKeyDown}
										autoComplete="off"
									/>
								</Grid2>
								<Grid2 size={{ md: 3, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={addNewComment}
										sx={{
											height: "40px",
											marginTop: 0.25,
											padding: "0 8px",
										}}
									>
										Add comment
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</DialogContent>
					<DialogActions
						sx={{
							padding: "0px 24px",
							marginTop: "16px",
							paddingBottom: "16px",
						}}
					>
						<Grid2 container spacing={2}>
							<Grid2
								sx={{ display: "flex", alignItems: "center" }}
								title={
									assetChanged
										? "You need to save to be able to manage resources"
										: ""
								}
								size={{ md: 4, xs: 4 }}
							>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									disabled={assetChanged}
									onClick={handleWorkOrderResourcesDialog}
									title={
										assetChanged
											? "You need to save to be able to manage resources"
											: ""
									}
								>
									MANAGE RESOURCES
								</Button>
							</Grid2>
							<Grid2
								container
								justifyContent="flex-end"
								spacing={2}
								sx={{ margin: 0 }}
								size={{ md: 8, xs: 8 }}
							>
								<Grid2 size={{ md: 6, xs: 6 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleCancel}
									>
										CANCEL
									</Button>
								</Grid2>
								<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 6 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="secondary"
										type="submit"
									>
										close order
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</DialogActions>
				</form>
				<TableWorkOrdesResoures
					show={openWorkOrdersResources}
					onHide={(shouldUpdate: boolean) => {
						setOpenWorkOrdersResources(false);
					}}
					AssetId={AssetId}
					WorkOrderId={WorkOrderId}
					WorkOrderName={data?.WorkOrderName as string}
					Title={data?.Title as string}
					AssetName={(data?.AssetName as string)?.replace(/.*\/(.+)$/, "$1")}
					TypeCode={data?.TypeCode as string}
					CraftId={data?.CraftId as number}
					OpenTimestamp={data?.OpenTimestamp as Date}
					CloseTimestamp={data?.CloseTimestamp as Date}
				/>
			</Dialog>
			<Snackbar
				open={closeSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="warning" onClose={handleClose}>
					Work order closed
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={linkAttachmentError}
				onHide={() => setLinkAttachmentError("")}
			/>
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
}
